import { useState, useRef, useEffect, useCallback } from 'react';
import PDFJSExpress from '@pdftron/pdfjs-express-viewer';
import { useNotifications, buildWordSeparateSearchPattern } from '@medprompt/shared';

/** PDF.js Express viewer lifecycle for the open document, plus quote search into it. */
const useDocumentViewer = ({ documentId, doc, documentService, isEditingEnabled }) => {
  const { addNotification } = useNotifications();
  const [pdfViewerReady, setPdfViewerReady] = useState(false);
  const viewerRef = useRef(null);
  const pdfInstanceRef = useRef(null);
  const pdfSearchListenerRef = useRef(null); // Active quote-search listener (sentence → word-separate fallback)
  const blobUrlRef = useRef(null); // Track blob URL for cleanup
  const documentLoadedHandlerRef = useRef(null); // Track event handler for cleanup
  const rotationHandlerRef = useRef(null); // Track rotation event handler for cleanup
  const copyHandlerRef = useRef(null); // Track copy-normalisation handler for cleanup

  // The viewer lives for the whole document, so its rotation handler reads these at event time.
  const isEditingEnabledRef = useRef(isEditingEnabled);
  const addNotificationRef = useRef(addNotification);
  useEffect(() => {
    isEditingEnabledRef.current = isEditingEnabled;
    addNotificationRef.current = addNotification;
  }, [isEditingEnabled, addNotification]);

  const docId = doc?.documentId;
  const docFileType = doc?.fileType;

  // Initialize PDF viewer when document is loaded
  useEffect(() => {
    if (!docId || docId !== documentId || !viewerRef.current) {
      console.log('PDF initialization skipped - documentId:', docId, 'viewerRef:', !!viewerRef.current);
      setPdfViewerReady(false);
      return;
    }

    // Set when this effect is torn down, so an initialization still awaiting the file or the
    // viewer for a previous document stops instead of mounting into the new one.
    let cancelled = false;
    const container = viewerRef.current;
    const extension = docFileType?.toLowerCase() || 'pdf';

    setPdfViewerReady(false);

    const cleanupAndInitialize = async () => {
      // Force cleanup of any existing instance and wait for it to complete
      if (pdfInstanceRef.current) {
        console.log('Cleaning up existing PDF viewer before new initialization');
        try {
          if (documentLoadedHandlerRef.current && pdfInstanceRef.current?.Core?.documentViewer) {
            pdfInstanceRef.current.Core.documentViewer.removeEventListener('documentLoaded', documentLoadedHandlerRef.current);
            documentLoadedHandlerRef.current = null;
          }

          await new Promise((resolve) => {
            pdfInstanceRef.current.dispose?.();
            pdfInstanceRef.current = null;
            // Give a small delay to ensure cleanup completes
            setTimeout(resolve, 100);
          });
        } catch (error) {
          console.warn('Error disposing existing PDF instance:', error);
        }
      }

      // Revoke any existing blob URL to free memory
      if (blobUrlRef.current) {
        console.log('Revoking previous blob URL');
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }

      if (cancelled) {
        console.log('Document or viewer changed during cleanup, aborting initialization');
        return;
      }

      console.log('Initializing PDF viewer for document:', docId);

      try {
        const blob = await documentService.fetchDocumentFile(docId);
        console.log('Document file blob received:', !!blob, 'size:', blob?.size, 'type:', blob?.type);

        if (!blob) {
          console.error('No blob received for document file');
          setPdfViewerReady(false);
          return;
        }

        if (cancelled) {
          console.log('Viewer ref no longer available or document changed, skipping PDF initialization');
          return;
        }

        const instance = await PDFJSExpress({
          path: '/pdfjsexpress',
          licenseKey: process.env.REACT_APP_PDF_VIEWER_KEY,
        }, container);

        if (cancelled) {
          console.log('Document changed during PDF initialization, disposing...');
          instance.dispose?.();
          return;
        }

        pdfInstanceRef.current = instance;
        console.log('PDF viewer initialized successfully');

        instance.Core.disableEmbeddedJavaScript();

        // Intercept copy events so that OCR-layered newlines become spaces
        // rather than landing in the clipboard as individual lines.
        const iframeWindow = instance.UI.iframeWindow;
        if (iframeWindow) {
          copyHandlerRef.current = (e) => {
            const selectedText = instance.Core.documentViewer.getSelectedText?.();
            if (selectedText) {
              const normalized = selectedText.replace(/\n/g, ' ').replace(/ {2,}/g, ' ').trim();
              e.clipboardData.setData('text/plain', normalized);
              e.preventDefault();
            }
          };
          iframeWindow.addEventListener('copy', copyHandlerRef.current);
        }

        instance.Core.documentViewer.setSearchHighlightColors({
          searchResult: 'rgba(255, 235, 59, 0.45)',
          activeSearchResult: 'rgba(255, 193, 7, 0.65)',
        });

        // Create blob URL and store reference for cleanup
        const blobUrl = URL.createObjectURL(blob);
        blobUrlRef.current = blobUrl;

        instance.Core.documentViewer.loadDocument(blobUrl, { extension });

        // Set default zoom using median page width across all pages.
        // FitWidth on page 1 alone can produce extreme zoom (e.g. 500%) when the
        // first page is a tiny embedded image or narrow cover. Instead we:
        //   1. Apply FitWidth briefly to get a reference zoom anchored to page 1.
        //   2. Derive the equivalent FitWidth zoom for every page.
        //   3. Take the median of those values (majority vote).
        //   4. Cap and apply as a fixed zoom level.
        documentLoadedHandlerRef.current = () => {
          try {
            const documentViewer = instance.Core.documentViewer;
            const pdfDoc = documentViewer.getDocument();

            instance.UI.setFitMode(instance.UI.FitMode.FitWidth);
            const page1Zoom = documentViewer.getZoom();
            const page1Width = pdfDoc.getPageInfo(1)?.width;

            if (page1Width && page1Zoom) {
              const pageCount = documentViewer.getPageCount();

              const perPageZooms = [];
              for (let i = 1; i <= pageCount; i++) {
                const info = pdfDoc.getPageInfo(i);
                if (info?.width) {
                  perPageZooms.push(page1Zoom * (page1Width / info.width));
                }
              }

              perPageZooms.sort((a, b) => a - b);
              const medianZoom = perPageZooms[Math.floor(perPageZooms.length / 2)];

              const MAX_ZOOM = 2.0;
              const finalZoom = Math.min(medianZoom, MAX_ZOOM);

              console.log(`PDF zoom: page1=${(page1Zoom * 100).toFixed(0)}%, median=${(medianZoom * 100).toFixed(0)}%, final=${(finalZoom * 100).toFixed(0)}%`);

              if (Math.abs(finalZoom - page1Zoom) > 0.05) {
                instance.UI.setFitMode(instance.UI.FitMode.Zoom);
                documentViewer.zoomTo(finalZoom);
              }
            } else {
              console.log(`PDF zoom set to fit width (page info unavailable)`);
            }
          } catch (error) {
            console.warn('Failed to set PDF zoom:', error);
          }
          setPdfViewerReady(true);
        };
        instance.Core.documentViewer.addEventListener('documentLoaded', documentLoadedHandlerRef.current);

        // Persist rotation changes to the server.
        // Note: rotation parameter is ABSOLUTE rotation state (0=0°, 1=90°, 2=180°, 3=270°), not a delta
        rotationHandlerRef.current = async (rotation) => {
          const absoluteDegrees = rotation * 90;
          console.log('PDF absolute rotation is now:', absoluteDegrees, 'degrees (rotation state:', rotation, ')');

          // Editing requires a READY document and write permission
          if (!isEditingEnabledRef.current()) {
            console.warn('Cannot save rotation - document is not in READY state or user lacks write permissions');
            addNotificationRef.current(
              `rotation-disabled-${docId}`,
              'Document rotation did not save - document needs to be \'ready\' first',
              { isError: true }
            );
            return;
          }

          try {
            await documentService.rotateDocument(docId, absoluteDegrees);
            console.log(`Successfully set document ${docId} rotation to absolute ${absoluteDegrees} degrees`);
          } catch (error) {
            console.error('Error saving document rotation:', error);
            addNotificationRef.current(`rotation-error-${docId}`, 'Failed to save rotation', { isError: true });
          }
        };
        instance.Core.documentViewer.addEventListener('rotationUpdated', rotationHandlerRef.current);
      } catch (error) {
        console.error('Error in PDF initialization:', error);
        if (!cancelled) {
          setPdfViewerReady(false);
        }
      }
    };

    cleanupAndInitialize();

    // Cleanup function - CRITICAL for preventing memory leaks
    return () => {
      console.log('PDF viewer cleanup triggered');
      cancelled = true;

      if (documentLoadedHandlerRef.current && pdfInstanceRef.current?.Core?.documentViewer) {
        try {
          pdfInstanceRef.current.Core.documentViewer.removeEventListener('documentLoaded', documentLoadedHandlerRef.current);
          documentLoadedHandlerRef.current = null;
        } catch (error) {
          console.warn('Error removing documentLoaded event listener:', error);
        }
      }

      if (rotationHandlerRef.current && pdfInstanceRef.current?.Core?.documentViewer) {
        try {
          pdfInstanceRef.current.Core.documentViewer.removeEventListener('rotationUpdated', rotationHandlerRef.current);
          rotationHandlerRef.current = null;
        } catch (error) {
          console.warn('Error removing rotationUpdated event listener:', error);
        }
      }

      if (copyHandlerRef.current && pdfInstanceRef.current?.UI?.iframeWindow) {
        try {
          pdfInstanceRef.current.UI.iframeWindow.removeEventListener('copy', copyHandlerRef.current);
        } catch (error) {
          console.warn('Error removing copy event listener:', error);
        }
        copyHandlerRef.current = null;
      }

      if (pdfInstanceRef.current) {
        console.log('Disposing PDF viewer instance');
        try {
          if (pdfSearchListenerRef.current) {
            pdfInstanceRef.current.UI?.removeSearchListener?.(pdfSearchListenerRef.current);
            pdfSearchListenerRef.current = null;
          }
          pdfInstanceRef.current.dispose?.();
        } catch (error) {
          console.warn('Error in PDF disposal:', error);
        }
        pdfInstanceRef.current = null;
      }

      // Revoke blob URL to free memory - THIS IS CRITICAL
      if (blobUrlRef.current) {
        console.log('Revoking blob URL');
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }

      // Clear viewer container to help garbage collection
      try {
        container.innerHTML = '';
      } catch (error) {
        console.warn('Error clearing viewer container:', error);
      }

      setPdfViewerReady(false);
    };
  }, [docId, docFileType, documentId, documentService]);

  // Handle search in PDF document. Sentence search first; if no hits (common on
  // OCR/scanned PDFs), fall back to word-separate regex so individual words still highlight.
  const handlePdfSearch = useCallback((phrase) => {
    const instance = pdfInstanceRef.current;
    if (!instance || !phrase?.trim()) {
      console.warn('PDF instance not available or no phrase provided');
      return;
    }

    try {
      const trimmedPhrase = phrase.trim();
      const wordSeparatePattern = buildWordSeparateSearchPattern(trimmedPhrase);
      console.log(`Searching PDF for phrase: ${trimmedPhrase}`);

      const runSearch = (searchValue, options) => {
        instance.UI.searchTextFull(searchValue, options);
        // Close the search panel immediately so only highlights remain
        instance.UI.closeElements(['searchPanel']);
      };

      const sentenceSearchOptions = {
        caseSensitive: false,
        wholeWord: false,
        wildcard: false,
        regex: false,
      };

      if (pdfSearchListenerRef.current) {
        instance.UI.removeSearchListener(pdfSearchListenerRef.current);
        pdfSearchListenerRef.current = null;
      }

      const onSearchComplete = (searchValue, options, results) => {
        instance.UI.removeSearchListener(onSearchComplete);
        if (pdfSearchListenerRef.current === onSearchComplete) {
          pdfSearchListenerRef.current = null;
        }

        const isSentenceSearch = searchValue === trimmedPhrase && !options?.regex;
        if (isSentenceSearch && (!results || results.length === 0) && wordSeparatePattern) {
          console.log(`No sentence matches; falling back to word-separate search: ${wordSeparatePattern}`);
          runSearch(wordSeparatePattern, {
            caseSensitive: false,
            wholeWord: false,
            wildcard: false,
            regex: true,
          });
        }
      };

      pdfSearchListenerRef.current = onSearchComplete;
      instance.UI.addSearchListener(onSearchComplete);
      runSearch(trimmedPhrase, sentenceSearchOptions);
    } catch (error) {
      console.error('Error searching PDF:', error);
    }
  }, []);

  return { viewerRef, pdfViewerReady, handlePdfSearch };
};

export default useDocumentViewer;
